// DS: MO2.4 Core Sale Branded Button
import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import BrandedButton, { BrandedButtonProps } from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiData, type BiEvent, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { Promo } from '@wix/da-react-context/pkg/publicSession';
import { useCurrentPromo } from '../../../../utils/hooks/useCurrentPromo';
import noop from '../../../../utils/noop';
import SaleIndicator, { type SaleIndicatorProps } from '../../lego/SaleIndicator';
import s from './BuyCoreBrandedButton.scss';

export interface Props extends Omit<BrandedButtonProps, 'variant' | 'onClick' | 'biData'> {
  promo?: Promo;
  className?: string;
  onClick?: () => void;
  biData?: BiEvent;
  /**
   * Upon clicking this button, the user will be directed to purchase core. Upon completing that flow, they'll be returned
   * to whatever URL is passed in this prop.
   */
  referer?: string;
  withSaleSparkles?: SaleIndicatorProps['withSparkles'];
  withSaleIndicator?: boolean;
  withMobileDefaultLabel?: boolean;
}

const BuyCoreBrandedButton: React.FC<Props> = ({
  promo,
  className,
  biData,
  referer,
  size,
  onClick = noop,
  children,
  withSaleSparkles,
  withSaleIndicator = true,
  withMobileDefaultLabel = true,
  ...props
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const currentPromo = useCurrentPromo('self') || promo;

  const buttonLabel = isMobile && withMobileDefaultLabel ? t('common.getCore') : children;

  return (
    <BrandedButton
      {...props}
      size={size}
      className={c(className, s[`root-${size}`])}
      variant="core-orange"
      href={Url.coreMembershipLink({ referer })}
      target="_blank"
      onClick={onClick}
      biData={BiData<BuyCoreButtonBiEvent>({
        evid: 375,
        ...biData,
      } as any)}
    >
      {buttonLabel}
      {withSaleIndicator && currentPromo?.discountValuePercent && (
        <SaleIndicator
          className={s['sale']}
          size={size === 'small' ? 'small' : 'medium'}
          background="black"
          discount={currentPromo.discountValuePercent}
          withSparkles={withSaleSparkles}
        />
      )}
    </BrandedButton>
  );
};
BuyCoreBrandedButton.displayName = 'BuyCoreButton';

export default BuyCoreBrandedButton;
